import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiCalendar, FiPlus, FiBarChart2, FiRefreshCw, FiFilter,
  FiUsers, FiTrendingUp, FiLock, FiUnlock, FiTrash2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoCriancasService, congregacaoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ReuniaoCriancas.css';

const parseLocalDate = (iso) => {
  if (!iso) return null;
  const s = String(iso).substring(0, 10);
  const [ano, mes, dia] = s.split('-').map(Number);
  return new Date(ano, mes - 1, dia);
};

const formatarData = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatarDiaSemana = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return '';
  const dias = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
  return dias[d.getDay()];
};

const ReuniaoCriancasList = () => {
  const navigate = useNavigate();
  const { canWriteModule } = useAuth();
  const podeEditar = canWriteModule('reuniao_criancas');

  const [reunioes, setReunioes] = useState([]);
  const [congregacoes, setCongregacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCong, setFiltroCong] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('abertas');
  const [showModal, setShowModal] = useState(false);
  const [novaData, setNovaData] = useState('');
  const [novaCong, setNovaCong] = useState('');
  const [novoHorario, setNovoHorario] = useState('09:00');
  const [criando, setCriando] = useState(false);

  const carregar = async () => {
    try {
      setLoading(true);
      const resp = await reuniaoCriancasService.listar();
      setReunioes(Array.isArray(resp) ? resp : resp.data || []);
    } catch {
      toast.error('Erro ao carregar reunioes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    congregacaoService.getAtivas().then((r) => {
      const nomes = (r?.data || []).map((c) => c.nome || c.nomeCompleto).filter(Boolean);
      setCongregacoes(nomes);
    }).catch(() => {});
  }, []);

  const reunioesFiltradas = useMemo(() => {
    const filtradas = reunioes.filter((r) => {
      if (filtroCong && r.congregacaoNome !== filtroCong) return false;
      if (filtroStatus === 'abertas'    && r.fechado) return false;
      if (filtroStatus === 'preenchidas' && ((r.totalPresentes || 0) === 0 || r.fechado)) return false;
      if (filtroStatus === 'pendentes'  && ((r.totalPresentes || 0) > 0 || r.fechado)) return false;
      if (filtroStatus === 'fechadas'   && !r.fechado) return false;
      return true;
    });
    return [...filtradas].sort((a, b) => {
      const da = String(a.dataReuniao).substring(0, 10);
      const db = String(b.dataReuniao).substring(0, 10);
      return da.localeCompare(db);
    });
  }, [reunioes, filtroCong, filtroStatus]);

  const kpis = useMemo(() => {
    const total = reunioesFiltradas.length;
    const comDados = reunioesFiltradas.filter((r) => (r.totalPresentes || 0) > 0).length;
    const totalCriancas = reunioesFiltradas.reduce((acc, r) => acc + (r.totalPresentes || 0), 0);
    const media = comDados > 0 ? Math.round(totalCriancas / comDados) : 0;
    return { total, comDados, totalCriancas, media };
  }, [reunioesFiltradas]);

  const criarReuniao = async () => {
    if (!novaData || !novaCong) {
      toast.error('Preencha a data e a congregacao');
      return;
    }
    try {
      setCriando(true);
      const r = await reuniaoCriancasService.criar({ dataReuniao: novaData, congregacaoNome: novaCong, horario: novoHorario });
      toast.success('Reuniao criada!');
      setShowModal(false);
      setNovaData(''); setNovaCong(''); setNovoHorario('09:00');
      navigate(`/reuniao-criancas/${r.id}`);
    } catch (err) {
      toast.error(err?.message || 'Erro ao criar reuniao');
    } finally {
      setCriando(false);
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Confirma exclusao da reuniao?')) return;
    try {
      await reuniaoCriancasService.excluir(id);
      toast.success('Reuniao excluida');
      carregar();
    } catch {
      toast.error('Erro ao excluir reuniao');
    }
  };

  return (
    <div className="rc-page">
      <header className="rc-page-header">
        <div>
          <h1><FiCalendar className="rc-icon-title" /> Reuniao de Criancas</h1>
          <p>Presenca, palavra do dia e acompanhamento por congregacao</p>
        </div>
        <div className="rc-header-actions">
          <Link to="/reuniao-criancas/dashboard" className="rc-btn rc-btn-ghost">
            <FiBarChart2 /> Dashboard
          </Link>
          <Link to="/reuniao-criancas/agenda" className="rc-btn rc-btn-ghost">
            <FiCalendar /> Agenda
          </Link>
          {podeEditar && (
            <button className="rc-btn rc-btn-primary" onClick={() => setShowModal(true)}>
              <FiPlus /> Nova reuniao
            </button>
          )}
        </div>
      </header>

      <div className="rc-kpis">
        <div className="rc-kpi"><FiCalendar /><div><span className="rc-kpi-num">{kpis.total}</span><span className="rc-kpi-lbl">Reunioes</span></div></div>
        <div className="rc-kpi"><FiUsers /><div><span className="rc-kpi-num">{kpis.comDados}</span><span className="rc-kpi-lbl">Preenchidas</span></div></div>
        <div className="rc-kpi"><FiUsers /><div><span className="rc-kpi-num">{kpis.totalCriancas}</span><span className="rc-kpi-lbl">Criancas (total)</span></div></div>
        <div className="rc-kpi"><FiTrendingUp /><div><span className="rc-kpi-num">{kpis.media}</span><span className="rc-kpi-lbl">Media / reuniao</span></div></div>
      </div>

      <div className="rc-filtros">
        <FiFilter />
        <select value={filtroCong} onChange={(e) => setFiltroCong(e.target.value)}>
          <option value="">Todas as congregacoes</option>
          {congregacoes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={carregar} className="rc-btn-icone" title="Atualizar"><FiRefreshCw /></button>
      </div>

      <div className="rc-status-tabs">
        {[
          { key: 'abertas',     label: 'Todas abertas' },
          { key: 'pendentes',   label: 'Pendentes'     },
          { key: 'preenchidas', label: 'Preenchidas'   },
          { key: 'fechadas',    label: 'Fechadas'      },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`rc-status-tab ${filtroStatus === tab.key ? 'rc-status-tab--ativo' : ''}`}
            onClick={() => setFiltroStatus(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rc-empty">Carregando...</div>
      ) : reunioesFiltradas.length === 0 ? (
        <div className="rc-empty">
          <FiCalendar size={28} />
          <h3>Nenhuma reuniao</h3>
          <p>
            {reunioes.length === 0
              ? 'Use o botao "Agenda" para gerar reunioes automaticamente, ou cadastre uma manual.'
              : 'Nenhuma reuniao corresponde aos filtros.'}
          </p>
          {podeEditar && (
            <Link to="/reuniao-criancas/agenda" className="rc-btn rc-btn-primary">
              Configurar agenda
            </Link>
          )}
        </div>
      ) : (
        <div className="rc-cards-grid">
          {reunioesFiltradas.map((r) => {
            const preenchida = (r.totalPresentes || 0) > 0;
            return (
              <div key={r.id} className={`rc-card ${r.fechado ? 'rc-card--fechado' : ''}`}>
                <div className="rc-card-top">
                  <div className="rc-card-data">
                    <span className="rc-card-dia">{formatarData(r.dataReuniao)}</span>
                    <span className="rc-card-semana">{formatarDiaSemana(r.dataReuniao)} · {r.horario || '09:00'}</span>
                  </div>
                  {r.fechado ? (
                    <span className="rc-tag rc-tag-fechada"><FiLock size={12} /> Fechada</span>
                  ) : preenchida ? (
                    <span className="rc-tag rc-tag-ok">Preenchida</span>
                  ) : (
                    <span className="rc-tag rc-tag-pendente"><FiUnlock size={12} /> Pendente</span>
                  )}
                </div>

                <div className="rc-card-cong">{r.congregacaoNome}</div>

                {preenchida ? (
                  <div className="rc-card-stats">
                    <div><strong>{r.totalPresentes}</strong><span>criancas</span></div>
                    <div><strong>{r.totalMeninas || 0}</strong><span>meninas</span></div>
                    <div><strong>{r.totalMeninos || 0}</strong><span>meninos</span></div>
                  </div>
                ) : (
                  <div className="rc-card-stats rc-card-stats--vazio">
                    Nenhum dado registrado ainda.
                  </div>
                )}

                <div className="rc-card-acts">
                  <button
                    className="rc-btn rc-btn-primary"
                    onClick={() => navigate(`/reuniao-criancas/${r.id}`)}
                  >
                    {preenchida ? 'Ver / Editar' : 'Preencher'}
                  </button>
                  {podeEditar && !r.fechado && (
                    <button
                      className="rc-btn-icone rc-btn-danger"
                      onClick={() => excluir(r.id)}
                      title="Excluir"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="rc-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="rc-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Nova Reuniao de Criancas</h2>
            <label>
              <span>Data *</span>
              <input type="date" value={novaData} onChange={(e) => setNovaData(e.target.value)} />
            </label>
            <label>
              <span>Congregacao *</span>
              <select value={novaCong} onChange={(e) => setNovaCong(e.target.value)}>
                <option value="">Selecione...</option>
                {congregacoes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>
              <span>Horario</span>
              <input type="time" value={novoHorario} onChange={(e) => setNovoHorario(e.target.value)} />
            </label>
            <div className="rc-modal-acts">
              <button className="rc-btn rc-btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="rc-btn rc-btn-primary" onClick={criarReuniao} disabled={criando}>
                {criando ? 'Criando...' : 'Criar reuniao'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReuniaoCriancasList;
